/**
 * TypedKnobForm — the typed-control half of the EffectLayerPanel knob editor
 * (Phase 3). Renders the KIND_KNOBS controls for a layer's kind (color pickers /
 * number inputs / range sliders / selects / colour lists) plus a modulator
 * sub-editor, patching the layer field-by-field via onPatch. The raw-JSON box in
 * EffectLayerPanel remains the escape hatch for kinds/fields not covered here.
 */
import React from 'react';
import type { EffectLayer } from '../../../../config/atmosphere/effectLayer.types';
import type { Modulator, ModTarget, ModShape } from '../../../../config/atmosphere/modulator';
import {
  KIND_KNOBS, numToHex, hexToNum, colorListToText, textToColorList, type KnobControl,
} from './effectKnobSchema';

interface Props {
  layer: EffectLayer;
  onPatch: (patch: Record<string, unknown>) => void;
}

const MOD_TARGETS: ModTarget[] = ['opacity', 'density', 'speed', 'tint'];
const MOD_SHAPES: ModShape[] = ['sine', 'pulse', 'ramp', 'noise'];
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export const TypedKnobForm: React.FC<Props> = ({ layer, onPatch }) => {
  const knobs = KIND_KNOBS[layer.kind];
  const rec = layer as unknown as Record<string, unknown>;
  const mods: Modulator[] = Array.isArray(rec.modulators) ? (rec.modulators as Modulator[]) : [];

  const setMods = (next: Modulator[]) => onPatch({ modulators: next });
  const addMod = () => setMods([...mods, { target: 'opacity', shape: 'sine', periodMs: 4000, min: 0.6, max: 1 }]);
  const patchMod = (i: number, p: Partial<Modulator>) => setMods(mods.map((m, j) => (j === i ? { ...m, ...p } : m)));
  const delMod = (i: number) => setMods(mods.filter((_, j) => j !== i));

  return (
    <div style={S.form} onClick={stop}>
      {knobs && knobs.map((c) => <Control key={c.key} c={c} value={rec[c.key]} onPatch={onPatch} />)}
      {!knobs && <div style={S.noSchema}>No typed controls for “{layer.kind}” yet — use Advanced (raw JSON) below.</div>}

      {/* Modulators — universal (every layer can breathe). */}
      <div style={S.modHead}>
        <span>modulators ({mods.length})</span>
        <button style={S.addModBtn} onClick={addMod}>＋ add</button>
      </div>
      {mods.map((m, i) => (
        <div key={i} style={S.modRow}>
          <select style={S.modSel} value={m.target} onChange={(e) => patchMod(i, { target: e.target.value as ModTarget })}>
            {MOD_TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select style={S.modSel} value={m.shape} onChange={(e) => patchMod(i, { shape: e.target.value as ModShape })}>
            {MOD_SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input style={S.modNum} type="number" title="periodMs" value={m.periodMs}
            onChange={(e) => patchMod(i, { periodMs: Number(e.target.value) })} />
          <input style={S.modNum} type="number" title="min" step={0.05} value={m.min}
            onChange={(e) => patchMod(i, { min: Number(e.target.value) })} />
          <input style={S.modNum} type="number" title="max" step={0.05} value={m.max}
            onChange={(e) => patchMod(i, { max: Number(e.target.value) })} />
          <button style={S.delModBtn} title="remove" onClick={() => delMod(i)}>✕</button>
        </div>
      ))}
    </div>
  );
};

const Control: React.FC<{ c: KnobControl; value: unknown; onPatch: (p: Record<string, unknown>) => void }> = ({ c, value, onPatch }) => {
  const label = c.label ?? c.key;
  if (c.type === 'color') {
    return (
      <label style={S.row}>
        <span style={S.label}>{label}</span>
        <input type="color" value={numToHex(typeof value === 'number' ? value : undefined)}
          onChange={(e) => onPatch({ [c.key]: hexToNum(e.target.value) })} />
      </label>
    );
  }
  if (c.type === 'colorList') {
    return (
      <label style={S.row}>
        <span style={S.label}>{label}</span>
        <input style={S.text} value={colorListToText(value)} placeholder="#rrggbb, #rrggbb"
          onChange={(e) => onPatch({ [c.key]: textToColorList(e.target.value) })} />
      </label>
    );
  }
  if (c.type === 'select') {
    return (
      <label style={S.row}>
        <span style={S.label}>{label}</span>
        <select style={S.sel} value={typeof value === 'string' ? value : ''}
          onChange={(e) => onPatch({ [c.key]: e.target.value })}>
          {(c.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (c.type === 'range') {
    const v = typeof value === 'number' ? value : (c.min ?? 0);
    return (
      <label style={S.row}>
        <span style={S.label}>{label}</span>
        <input style={{ flex: 1 }} type="range" min={c.min ?? 0} max={c.max ?? 1} step={c.step ?? 0.02}
          value={v} onChange={(e) => onPatch({ [c.key]: parseFloat(e.target.value) })} />
        <span style={S.val}>{v.toFixed(2)}</span>
      </label>
    );
  }
  // number
  const nv = typeof value === 'number' ? value : '';
  return (
    <label style={S.row}>
      <span style={S.label}>{label}</span>
      <input style={S.num} type="number" step={c.step ?? 1} value={nv}
        onChange={(e) => onPatch({ [c.key]: e.target.value === '' ? undefined : Number(e.target.value) })} />
    </label>
  );
};

const S: Record<string, React.CSSProperties> = {
  form: { display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6 },
  row: { display: 'flex', alignItems: 'center', gap: 6 },
  label: { color: '#9aa', fontSize: 10, minWidth: 78 },
  val: { color: '#ddd', fontSize: 10, minWidth: 30, textAlign: 'right' },
  num: { width: 90, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 11, padding: '2px 4px' },
  text: { flex: 1, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 11, padding: '2px 4px' },
  sel: { flex: 1, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 11, padding: '2px 4px' },
  noSchema: { fontSize: 10, color: '#a98', fontStyle: 'italic' },
  modHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, color: '#9cb', fontSize: 11, borderTop: '1px solid #38516b', paddingTop: 6 },
  addModBtn: { background: 'rgba(80,160,120,0.25)', border: '1px solid #4a7', borderRadius: 4, color: '#cfe', fontSize: 10, cursor: 'pointer', padding: '2px 6px' },
  modRow: { display: 'flex', alignItems: 'center', gap: 3 },
  modSel: { background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 10, padding: '2px 2px' },
  modNum: { width: 56, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 10, padding: '2px 3px' },
  delModBtn: { width: 20, height: 20, background: '#a44', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10 },
};
